import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios, { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

interface FormFieldDef {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: string[] | null;
  placeholder?: string | null;
}

interface LandingForm {
  publicToken: string | null;
  title: string | null;
  introText: string | null;
  thankYouText: string | null;
  fieldConfig: FormFieldDef[];
}

interface LandingBranding {
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  themeTokensJson: string | null;
}

interface PublicLanding {
  name: string;
  locale: string;
  variant: string;
  heroHeadline: string;
  heroImageUrl: string | null;
  bodyMd: string | null;
  thankYouMessage: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
  branding: LandingBranding | null;
  form: LandingForm | null;
}

interface BlockDto {
  type: string;
  config: Record<string, string | undefined>;
}

interface ServeResponse {
  name: string;
  locale: string;
  variantKey: string;
  contextValue: string;
  visitorId: string;
  blocks: BlockDto[];
  formToken: string | null;
}

const STYLE_ID = 'kairos-public-tenant-theme';
const VID_KEY = 'kairos.lvid';
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ad_id',
  'ad_set_id',
] as const;

/**
 * Story 109.1 — Kairos-hosted campaign landing page (public, no auth,
 * no sidebar). Mounted at /l/:tenantSlug/:pageSlug. Reads ?variant=a|b
 * for A/B and ?lang=en|es|pt-BR for locale, applies tenant branding,
 * renders the authored hero/body and the NATIVE lead-capture form, and
 * submits to the existing /api/v1/public/forms/{token} so UTM capture
 * and 105.5 attribution are reused unchanged.
 */
export function PublicCampaignLandingPage() {
  const { t } = useTranslation();
  const { tenantSlug, pageSlug } = useParams<{ tenantSlug: string; pageSlug: string }>();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState<PublicLanding | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);

  const variant = searchParams.get('variant');
  const lang = searchParams.get('lang');

  // 109.1b — optimised serve: blocks + bandit/multivariate variant +
  // cross-domain visitor id. Falls back to the lite render below when
  // the page has no running experiment / no blocks.
  const [serve, setServe] = useState<ServeResponse | null>(null);
  const [serveForm, setServeForm] = useState<LandingForm | null>(null);

  useEffect(() => {
    if (!tenantSlug || !pageSlug) return;
    const vid = localStorage.getItem(VID_KEY);
    const qs = new URLSearchParams(window.location.search);
    if (lang) qs.set('locale', lang);
    if (vid) qs.set('vid', vid);
    let cancelled = false;
    axios
      .get<ServeResponse>(
        `/api/v1/public/landing/${tenantSlug}/${pageSlug}/serve?${qs.toString()}`,
      )
      .then(async ({ data }) => {
        if (cancelled) return;
        localStorage.setItem(VID_KEY, data.visitorId);
        setServe(data);
        if (data.formToken) {
          const { data: f } = await axios.get<LandingForm>(
            `/api/v1/public/forms/${data.formToken}`,
          );
          if (!cancelled) setServeForm({ ...f, publicToken: data.formToken });
        }
      })
      .catch(() => {
        /* no experiment — lite render path takes over */
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, pageSlug, lang]);

  useEffect(() => {
    if (!page?.branding) return;
    const branding = page.branding;
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }
    if (branding.themeTokensJson) {
      try {
        const tokens = JSON.parse(branding.themeTokensJson) as {
          light?: Record<string, string>;
          dark?: Record<string, string>;
        };
        const blocks: string[] = [];
        if (tokens.light) {
          blocks.push(
            `:root { ${Object.entries(tokens.light)
              .map(([k, v]) => `${k}: ${v};`)
              .join(' ')} }`,
          );
        }
        if (tokens.dark) {
          blocks.push(
            `html.dark { ${Object.entries(tokens.dark)
              .map(([k, v]) => `${k}: ${v};`)
              .join(' ')} }`,
          );
        }
        style.textContent = blocks.join('\n');
      } catch {
        /* ignore malformed tokens */
      }
    }
    return () => {
      style?.remove();
    };
  }, [page]);

  useEffect(() => {
    if (!tenantSlug || !pageSlug) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const params = new URLSearchParams();
    if (variant) params.set('variant', variant);
    if (lang) params.set('locale', lang);
    axios
      .get<PublicLanding>(
        `/api/v1/public/landing/${tenantSlug}/${pageSlug}?${params.toString()}`,
      )
      .then(({ data }) => {
        if (cancelled) return;
        setPage(data);
        const initial: Record<string, string> = {};
        for (const f of data.form?.fieldConfig ?? []) initial[f.name] = '';
        setValues(initial);
      })
      .catch(() => {
        if (!cancelled) setLoadError(t('landing.public.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantSlug, pageSlug, variant, lang, t]);

  function setValue(name: string, value: string) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  const activeForm = serveForm ?? page?.form ?? null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const token = activeForm?.publicToken;
    if (!token) return;
    setSubmitError(null);
    setSubmitting(true);
    const payload: Record<string, string | undefined> = {};
    for (const field of activeForm?.fieldConfig ?? []) {
      const v = values[field.name]?.trim();
      if (v) payload[field.name] = v;
    }
    for (const key of UTM_KEYS) {
      const v = searchParams.get(key);
      if (v) payload[key] = v;
    }
    try {
      const { data } = await axios.post<{ ok: boolean; message: string | null }>(
        `/api/v1/public/forms/${token}`,
        payload,
      );
      // 109.1b — close the bandit loop: a successful submit is the
      // conversion signal for the served variant.
      if (serve) {
        void axios.post(
          `/api/v1/public/landing/${tenantSlug}/${pageSlug}/event`,
          {
            type: 'CONVERSION',
            variantKey: serve.variantKey,
            contextValue: serve.contextValue,
            visitorId: serve.visitorId,
          },
        );
      }
      setSubmittedMessage(
        data.message ?? page?.thankYouMessage ?? t('landing.public.thankYou'),
      );
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 429) {
        setSubmitError(t('landing.public.rateLimited'));
      } else {
        setSubmitError(t('landing.public.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // 109.1b — block-driven render (CMS + experiment). Takes precedence
  // when a running experiment served ordered blocks.
  if (serve && serve.blocks.length > 0) {
    return (
      <div
        className="min-h-screen"
        style={{
          backgroundColor: 'var(--color-surface-1)',
          color: 'var(--color-on-surface)',
        }}
      >
        <div className="mx-auto max-w-2xl px-4 py-12">
          {page?.branding?.logoUrl && (
            <div className="mb-8 flex justify-center">
              <img src={page.branding.logoUrl} alt={serve.name} className="h-14 w-auto" />
            </div>
          )}
          {serve.blocks.map((b, i) => (
            <BlockView key={i} block={b} />
          ))}
          {activeForm?.publicToken && (
            <div
              className="mt-8 rounded-xl p-6"
              style={{ backgroundColor: 'var(--color-surface-2)' }}
            >
              {submittedMessage ? (
                <p className="text-center text-base">{submittedMessage}</p>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {activeForm.title && (
                    <h2 className="text-lg font-semibold">{activeForm.title}</h2>
                  )}
                  {activeForm.fieldConfig.map((field) => (
                    <FieldInput
                      key={field.name}
                      field={field}
                      value={values[field.name] ?? ''}
                      onChange={(v) => setValue(field.name, v)}
                    />
                  ))}
                  {submitError && (
                    <p className="text-sm text-destructive">{submitError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-lg px-4 py-3 text-sm font-medium transition disabled:opacity-60"
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      color: 'var(--color-primary-foreground)',
                    }}
                  >
                    {submitting
                      ? t('landing.public.sending')
                      : t('landing.public.submit')}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: 'var(--color-surface-1)' }}
      >
        <p className="text-sm" style={{ color: 'var(--color-on-surface-muted)' }}>
          {t('landing.public.loading')}
        </p>
      </div>
    );
  }

  if (loadError || !page) {
    return (
      <div
        className="flex min-h-screen items-center justify-center p-4"
        style={{ backgroundColor: 'var(--color-surface-1)' }}
      >
        <p className="text-center text-sm text-destructive">
          {loadError ?? t('landing.public.loadError')}
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--color-surface-1)', color: 'var(--color-on-surface)' }}
    >
      <div className="mx-auto max-w-2xl px-4 py-12">
        {page.branding?.logoUrl && (
          <div className="mb-8 flex justify-center">
            <img src={page.branding.logoUrl} alt={page.name} className="h-14 w-auto" />
          </div>
        )}
        {page.heroImageUrl && (
          <img
            src={page.heroImageUrl}
            alt=""
            className="mb-8 w-full rounded-xl object-cover"
          />
        )}
        <h1 className="mb-6 text-center text-3xl font-bold">{page.heroHeadline}</h1>
        {page.bodyMd && (
          <div className="mb-10 space-y-4 text-base leading-relaxed">
            {page.bodyMd
              .split(/\n{2,}/)
              .map((para, i) => (
                <p key={i} style={{ color: 'var(--color-on-surface-muted)' }}>
                  {para}
                </p>
              ))}
          </div>
        )}

        <div
          className="rounded-xl p-6"
          style={{ backgroundColor: 'var(--color-surface-2)' }}
        >
          {submittedMessage ? (
            <p className="text-center text-base">{submittedMessage}</p>
          ) : page.form?.publicToken ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {page.form.title && (
                <h2 className="text-lg font-semibold">{page.form.title}</h2>
              )}
              {page.form.fieldConfig.map((field) => (
                <FieldInput
                  key={field.name}
                  field={field}
                  value={values[field.name] ?? ''}
                  onChange={(v) => setValue(field.name, v)}
                />
              ))}
              {submitError && (
                <p className="text-sm text-destructive">{submitError}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg px-4 py-3 text-sm font-medium transition disabled:opacity-60"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-primary-foreground)',
                }}
              >
                {submitting
                  ? t('landing.public.sending')
                  : t('landing.public.submit')}
              </button>
            </form>
          ) : (
            <p
              className="text-center text-sm"
              style={{ color: 'var(--color-on-surface-muted)' }}
            >
              {t('landing.public.noForm')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function BlockView({ block }: { block: BlockDto }) {
  const c = block.config;
  switch (block.type) {
    case 'HERO':
      return (
        <div className="mb-8 text-center">
          {c.imageUrl && (
            <img
              src={c.imageUrl}
              alt=""
              className="mb-6 w-full rounded-xl object-cover"
            />
          )}
          <h1 className="text-3xl font-bold">{c.headline}</h1>
          {c.subhead && (
            <p className="mt-2 text-base" style={{ color: 'var(--color-on-surface-muted)' }}>
              {c.subhead}
            </p>
          )}
        </div>
      );
    case 'TEXT':
      return (
        <div className="mb-6 space-y-4 text-base leading-relaxed">
          {(c.text ?? '').split(/\n{2,}/).map((p, i) => (
            <p key={i} style={{ color: 'var(--color-on-surface-muted)' }}>
              {p}
            </p>
          ))}
        </div>
      );
    case 'IMAGE':
      return c.url ? (
        <img src={c.url} alt={c.alt ?? ''} className="mb-6 w-full rounded-xl" />
      ) : null;
    case 'VIDEO':
      return c.url ? (
        <video src={c.url} controls className="mb-6 w-full rounded-xl" />
      ) : null;
    case 'CTA':
      return (
        <div className="mb-6 text-center">
          <a
            href={c.href ?? '#'}
            className="inline-block rounded-lg px-6 py-3 text-sm font-medium"
            style={{
              backgroundColor: 'var(--color-primary)',
              color: 'var(--color-primary-foreground)',
            }}
          >
            {c.label}
          </a>
        </div>
      );
    case 'SPACER':
      return <div style={{ height: c.height ?? '32px' }} />;
    case 'HTML':
      // tenant-authored markup for their own campaign page.
      return (
        <div
          className="mb-6"
          dangerouslySetInnerHTML={{ __html: c.html ?? '' }}
        />
      );
    default:
      return null;
  }
}

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: FormFieldDef;
  value: string;
  onChange: (v: string) => void;
}) {
  const cls = 'w-full rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2';
  const style: React.CSSProperties = {
    backgroundColor: 'var(--color-surface-1)',
    color: 'var(--color-on-surface)',
    border: '1px solid var(--color-divider)',
  };
  return (
    <div className="space-y-1">
      <label htmlFor={field.name} className="text-sm font-medium">
        {field.label}
        {field.required && (
          <span className="ml-1" style={{ color: 'var(--color-primary)' }}>
            *
          </span>
        )}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          id={field.name}
          rows={4}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={cls}
          style={style}
        />
      ) : field.type === 'dropdown' ? (
        <select
          id={field.name}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
          style={style}
        >
          <option value="">{field.placeholder ?? '—'}</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={field.name}
          type={inputType(field.type)}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? undefined}
          className={cls}
          style={style}
        />
      )}
    </div>
  );
}

function inputType(tp: string): string {
  switch (tp) {
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    default:
      return 'text';
  }
}
